import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { mapDetails } from './deviceFormHelpers';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  readersApi,
  sensorsApi,
  type Reader,
  type ReaderStatus,
  type Sensor,
  type SensorStatus,
} from '../../lib/api/devices';
import styles from './DeviceFormDialog.module.css';

/**
 * The status-change dialog — the ONLY path that moves a device from one
 * status to another (`devices.ts`'s own docblock: `PATCH` is refused a
 * `status` outright). Shared between sensors and readers, parameterised on
 * `kind` exactly as `DeviceFormDialog` is.
 *
 * ## The one transition that demands a reason
 *
 * `IN_STOCK` is the API's own "correction" target — moving a device BACK to
 * `in_stock` is not an ordinary lifecycle step (a device does not un-dispense
 * itself), and `SensorController`/`ReaderController` refuse the request with
 * a 422 naming `reason` when it is absent. This dialog enforces the same
 * rule client-side, so an agent sees "a reason is required" before spending a
 * round trip on it, not instead of the server check — the 422 path (a reason
 * supplied that the server still rejects for some other cause) is handled
 * exactly like every other field error, through `mapDetails`.
 *
 * Sensor and reader statuses are two distinct unions (`SensorStatus` ends
 * `expired`; `ReaderStatus` ends `retired` in its place) — the options list
 * below is chosen by `kind`, never merged into one shared list a caller
 * could accidentally send to the wrong endpoint.
 */

export interface DeviceStatusDialogProps {
  kind: 'sensor' | 'reader';
  open: boolean;
  device: Sensor | Reader;
  onClose: () => void;
  onChanged: (updated: Sensor | Reader) => void;
}

/** The one status that requires a reason — see this file's own docblock. */
const CORRECTION_STATUS = 'in_stock';

const SENSOR_STATUSES: readonly SensorStatus[] = [
  'in_stock',
  'assigned',
  'dispensed',
  'active',
  'expired',
  'faulty',
  'replaced',
  'returned',
];

const READER_STATUSES: readonly ReaderStatus[] = [
  'in_stock',
  'assigned',
  'dispensed',
  'active',
  'faulty',
  'replaced',
  'returned',
  'retired',
];

const FORBIDDEN_CODE = 'forbidden';

export function DeviceStatusDialog({ kind, open, device, onClose, onChanged }: DeviceStatusDialogProps) {
  const { t } = useTranslation();

  const [status, setStatus] = useState<string>(device.status);
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const statusRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!open) return;
    setStatus(device.status);
    setReason('');
    setReasonMissing(false);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, device]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('devices.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('devices.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('devices.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    // The client-side half of the one rule this dialog exists to enforce:
    // a reason is required for the correction target, checked before any
    // request is sent.
    if (status === CORRECTION_STATUS && reason.trim() === '') {
      setReasonMissing(true);
      return;
    }
    setReasonMissing(false);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const trimmedReason = reason.trim() === '' ? undefined : reason.trim();
      const updated =
        kind === 'sensor'
          ? await sensorsApi.setStatus(device.uuid, status as SensorStatus, trimmedReason)
          : await readersApi.setStatus(device.uuid, status as ReaderStatus, trimmedReason);
      onChanged(updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const statuses = kind === 'sensor' ? SENSOR_STATUSES : READER_STATUSES;
  const statusOptions: readonly SelectOption[] = statuses.map((value) => ({
    value,
    label: t(`devices.status.${value}`),
  }));

  const reasonError = reasonMissing
    ? t('devices.reasonRequiredForCorrection')
    : fieldErrors['reason'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      initialFocusRef={statusRef}
      title={t('devices.changeStatusTitle', { serial: device.serial_number })}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="deviceStatusFailure">
          {generalFailure}
        </div>

        <Select
          ref={statusRef}
          label={t('devices.fieldNewStatus')}
          width="fill"
          required
          options={statusOptions}
          value={status}
          error={fieldErrors['status']}
          onChange={(event) => {
            const { value } = event.target;
            setStatus(value);
          }}
        />

        <Input
          label={t('devices.fieldReason')}
          width="fill"
          required={status === CORRECTION_STATUS}
          value={reason}
          error={reasonError}
          hint={status === CORRECTION_STATUS ? undefined : t('devices.reasonOptionalHint')}
          onChange={(event) => {
            const { value } = event.target;
            setReason(value);
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<CheckIcon size={16} />} type="submit" loading={submitting}>
            {t('devices.confirm')}
          </Button>
          <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onClose}>
            {t('devices.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
